import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Moon, Sun, Menu, X } from "lucide-react";

const links = [
  { label: "About the Program", href: "#program", id: "program" },
  { label: "Our Philosophy", href: "#manifesto", id: "manifesto" },
  { label: "Your Mentor", href: "#mentor", id: "mentor" },
  { label: "Pricing", href: "#pricing", id: "pricing" },
  { label: "Contact Us", href: "#contact", id: "contact" },
];

export default function Header() {
  const [dark, setDark] = useState(() => localStorage.getItem("khoj_theme") === "dark");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("khoj_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="#top" className="flex items-center gap-3" data-testid="header-brand-logo">
            <span className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-border" data-testid="header-compass-mark">
              <img src="/logos/compass_mark.png" alt="TheKhoj compass logo" className="w-full h-full object-cover" />
            </span>
            <span className="leading-tight">
              <span className="block font-display font-800 text-lg font-extrabold tracking-tight">TheKhoj</span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground" data-testid="header-title-text">
                Career Discovery Program
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={`header-nav-link-${l.id}`}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle-button"
              aria-label="Toggle theme"
              className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors"
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <a
              href="#contact"
              data-testid="header-enquire-now-button"
              className="hidden sm:inline-flex text-sm font-semibold px-5 py-2.5 rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Enquire Now
            </a>
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background px-6 py-4 space-y-3" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block text-sm font-medium text-muted-foreground"
              data-testid={`mobile-nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </div>
      )}
    </motion.header>
  );
}
